import { memo, useEffect, useMemo, useRef, useState } from "react";
import { ReactSortable } from "react-sortablejs";
import { 
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  IconButton,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter,
  Spinner,
  Tooltip,
  useDisclosure,
  Text,
  Spacer,
  useColorModeValue,
  Thead,
  Th,
  Table,
  Tr,
  Td,
  UnorderedList,
  ListItem,
  Grid,
  GridItem,
  Tbody,
  InputGroup
} from '@chakra-ui/react'
import { customStyles } from "../common/Select";
import {
  AddIcon,
  CheckIcon
} from "@chakra-ui/icons";
import { Controller,
  useFieldArray,
  useForm
} from "react-hook-form";
import ReactSelect, { components } from "react-select";
import {
  SortableMultiValue,
  SortableMultiValueLabel,
  SortableSelect
} from "./SortableSelect";
import { arrayMove } from "react-sortable-hoc";
import { routingRulesSchema, rulesValidation } from "./validation";
import { yupResolver } from "@hookform/resolvers/yup";
import { applyRoutingRules } from "../../../fix-search/API";
import { DragHandle } from "./DragHandle";
import { defaultStyle as defaultAutocompleteInputStyle, FIXFieldsAutocompleteInput } from "../Autocomplete/FIXFieldsAutocompleteInput";
import { deepCopyAndApply } from "../../../fix-search/components/utils";
import { FiAlertTriangle, FiArrowRight, FiX } from "react-icons/fi";

const toOption = (val) => ({ value: val, label: val })

const red = 'var(--chakra-colors-service-red)'
const green = 'var(--chakra-colors-green-700)'

const Option = (props) => {
  if (props.value === 'ALL') {
    return (
      <Tooltip
        label='Potentially dangerous'
        shouldWrapChildren // To enable tooltip for disabled button
      >
        <components.Option
          {...props}
          children={
            <HStack justifyContent='start' spacing={1}>
              <FiAlertTriangle color={red}/> 
              <span>
                {props.children}
              </span>
            </HStack>
          }
        />
      </Tooltip>
    );
  }

  return <components.Option {...props} />
}

const State = {
  New: 'New',
  Clean: 'Clean',
  Resetting: 'Resetting',
  Dirty: 'Dirty',
  DirtyOutdated: 'DirtyOutdated',
  Validating: 'Validating',
  Submitting: 'Submitting',
}

const autocompleteInputStyle = defaultAutocompleteInputStyle

const autocompleteInputStyleError =
  deepCopyAndApply(autocompleteInputStyle,
    style => {
      style['&multiLine'].input.borderColor = red
      style['&multiLine'].input.boxShadow = `0 0 0 1px ${red}`
    }
  )
  
const autocompleteInputStyleDirty =
  deepCopyAndApply(autocompleteInputStyle,
    style => {
      style['&multiLine'].input.borderColor = green
      style['&multiLine'].input.boxShadow = `0 0 0 1px ${green}`
    }
  )

export const RoutingRules = memo(({ 
  fieldSuggestions,
  token,
  rules,
  setRules,
  routes,
  policyAllEnabled
}) => {
  const state = useRef(State.New)

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState,
    setValue,
    trigger,
    getValues
  } = useForm({
    defaultValues: {
      rules: rules
    },
    resolver: yupResolver(routingRulesSchema(token)),
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const {
    errors,
    dirtyFields,
    isValid,
    isValidating,
    isSubmitting
  } = formState;
  const {
    fields,
    append,
    remove,
    move
  } = useFieldArray({ name: 'rules', control });

  const policyOptions = useMemo(() => {
    const options = [
      {
        options: [ "ROUND_ROBIN", "ORDERED" ].map(toOption),
      }
    ]

    if (policyAllEnabled) {
      options.push({
        label: 'Danger',
        options: [ "ALL" ].map(toOption),
      })
    }

    return options
  }, [policyAllEnabled])

  const [newRulesAvailable, setNewRulesAvailable] = useState(false)

  const setValidationSuccess = (field) => {
    rulesValidation[field.id] = {
      previous: {
        pattern: field.pattern,
        result: { correct: true }
      }
    }
  }

  useEffect(() => {
    if (state.current !== State.New && state.current !== State.Resetting) {
      if (isDirty) {
        setNewRulesAvailable(true)
      } else {
        resetForm(rules)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules])

  useEffect(() => {
    setRulesOrder(fields.map((field) => ({ id: field.id, chosen: false })))

    if (state.current === State.Resetting) {
      fields.map(setValidationSuccess)
      state.current = State.Clean
    }
    trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields])

  useEffect(() => {
    fields.map(setValidationSuccess)
    state.current = State.Clean

    return () => state.current = State.New
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [])

  const drag = useRef({
    chosenIndex: -1,
    movedFrom: -1,
    movedId: undefined
  })

  const [rulesOrder, setRulesOrder] = useState([]);
  const [moveDetected, setMoveDetected] = useState(undefined)

  const handleRulesOrderChange = (newRulesOrder) => {
    const prevChosenIndex = drag.current.chosenIndex
    drag.current.chosenIndex = newRulesOrder.findIndex((rule) => rule.chosen)
    if (prevChosenIndex !== -1 && drag.current.chosenIndex === -1) {
      drag.current.movedFrom = prevChosenIndex
      drag.current.movedId = newRulesOrder[prevChosenIndex].id
      // rules order change will be triggered in a separate handleRulesOrderChange() call
      // (this is a sortablejs behaviour)
    }
    else if (drag.current.movedId) {
      setMoveDetected({
        from: drag.current.movedFrom,
        to: newRulesOrder.findIndex((rule) => rule.id === drag.current.movedId)
      })

      drag.current.movedId = undefined
      drag.current.movedFrom = -1
    }

    setRulesOrder(newRulesOrder)
  }

  useEffect(() => {
    if (moveDetected) {
      move(moveDetected.from, moveDetected.to)
      setMoveDetected(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moveDetected])

  const [submitSucceeded, setSubmitSucceeded] = useState(false);
    
  const resetForm = (rules) => {
    state.current=State.Resetting
    setNewRulesAvailable(false)
    reset({
      rules: rules
    })
  }

  const onValidFormSubmit = async (data) => {
    await applyRoutingRules(
      data.rules,
      () => {
        const newRules =
          data.rules
            .map(rule => (
              {
                ...rule,
                id: undefined
              }
            ))
        setRules(newRules)
        resetForm(newRules)
        setSubmitSucceeded(true)
        setTimeout(() => {
          setSubmitSucceeded(false)
        }, 1500)
      },
      (err) => {
        console.log('Failed to apply routing rules', err)
      })
  }

  const onInvalidFormSubmit = (err) => {
    console.log(getValues('rules'))
    console.log('Errors!', err)
  }

  const addRule = () => {
    append({
      pattern: '',
      policy: null,
      routes: []
    })
  }

  const isRoutesDirty = (dirty) => {
    return (dirty !== undefined
      && (dirty === true // if 'routes' field is cleared
        || dirty.length === 0 // if rule is newly added (and has empty 'routes' field by default)
        || dirty?.find(e => e))) // if new set of routes is assigned to 'routes' fields
  }

  const [previousRoutes, setPreviousRoutes] = useState([])

  useEffect(() => {
    if (JSON.stringify(routes) !== JSON.stringify(previousRoutes)) {
      setPreviousRoutes(routes)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes])

  const routesOptions = useMemo(
    () => previousRoutes.map(toOption)
  , [previousRoutes])

  // We implement custom 'isDirty', instead of using formState.isDirty,
  // because we need to ignore hidden 'id' field
  const isDirty = 
      !!dirtyFields.rules?.find(dirtyRule => dirtyRule && (dirtyRule.pattern || dirtyRule.policy || isRoutesDirty(dirtyRule.routes)))

  const {
    isOpen: isWarningModalOpen,
    onOpen: onOpenWarningModal,
    onClose: onCloseWarningModal
  } = useDisclosure()

  const routingRuleShadow = useColorModeValue('md', 'dark-lg')

  return (
    <form onSubmit={handleSubmit(onValidFormSubmit, onInvalidFormSubmit)}>
      <Modal
        isCentered
        isOpen={isWarningModalOpen}
        onClose={onCloseWarningModal}
        size='xl'
        scrollBehavior='inside'
      >
        <ModalOverlay />
        <ModalContent bg='background'>
          <ModalHeader>
            <HStack>
              <FiAlertTriangle color={red}/> 
              <Text>Attempt to overwrite changes on the server!</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody >
            <Text>Your changes are based on the old version of the routing rules.</Text>
            <Text>There is a new version of routing rules on the server:</Text>
            <Table
              marginTop={2}
              style={{ font: 'var(--code-font)' }}
            >
              <Thead>
                <Th>Pattern</Th>
                <Th>Policy</Th>
                <Th>Routes</Th>
              </Thead>
              <Tbody>
              {rules.map((rule) => (
                <Tr>
                  <Td>{rule.pattern}</Td>
                  <Td>{rule.policy}</Td>
                  <Td>
                    <UnorderedList>
                    {rule.routes.map((route) => <ListItem>{route}</ListItem>)}
                    </UnorderedList>
                  </Td>
                </Tr>
              ))}
              </Tbody>
            </Table>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} variant='outline' onClick={onCloseWarningModal}>
              Close
            </Button>
            <Button onClick={() => {
              handleSubmit(onValidFormSubmit, onInvalidFormSubmit)()
              onCloseWarningModal()
            }}>
              Overwrite
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <HStack
        marginBottom={2}
      >
        <Button
          leftIcon={<AddIcon />}
          onClick={addRule}
        >
          Add New Rule
        </Button>
        <Spacer />
        <Tooltip
          hasArrow
          label='Reverts back to last version'
        >
          <Button
            variant='outline'
            isDisabled={!(isDirty)}
            onClick={() => { resetForm(rules) }}
          >
            Discard
          </Button>
        </Tooltip>
        <Tooltip
          hasArrow
          label={isValidating ? 'Validation in progress' : !isDirty ? 'Rules are not changed' : !isValid ? 'Rules are not valid' : '' }
          shouldWrapChildren // To enable tooltip for disabled button
        >
          <Button
            variant={submitSucceeded ? 'outline' : 'solid'}
            isDisabled={!isDirty || !isValid || isValidating || isSubmitting}
            onClick={() => {
              if (newRulesAvailable) {
                onOpenWarningModal()
              } else {
                handleSubmit(onValidFormSubmit, onInvalidFormSubmit)()
              }
            }}
            rightIcon={
              submitSucceeded ? <CheckIcon color='green.500' /> :
              isValidating || isSubmitting ? <Spinner size='sm'/> :
              newRulesAvailable ? <FiAlertTriangle color={red}/> 
                : <FiArrowRight />
            }
          >
            Submit
          </Button>
        </Tooltip>
      </HStack>
      {fields.length === 0 
      ? (<Text fontSize="lg" textAlign="center" marginTop={5}>
          No routing rules defined
        </Text>)
      : (<>
      <ReactSortable
          list={rulesOrder}
          setList={handleRulesOrderChange}
          handle='.handle'
          animation={150}
          ghostClass='sortable-ghost'
      >
        {fields.map((rule, index) => (
          <HStack
            border='1px solid var(--chakra-colors-form-section-border)'
            key={rule.id}
            backgroundColor='var(--chakra-colors-secondary-background)'
            spacing={2} 
            fontFamily='mono' 
            marginTop={5}
            marginBottom={3}
            alignItems='flex-start'
            paddingX={2}
            paddingY={3} 
            shadow={routingRuleShadow} 
            borderRadius={3}
          >
            <DragHandle />
            <Grid
              templateRows='repeat(1, 1fr)'
              templateColumns='repeat(12, 1fr)'
              gap={4}
              width='100%'
            >
            <GridItem rowSpan={1} colSpan={7}>
              <FormControl isInvalid={errors.rules?.[index]?.pattern}>
                <InputGroup>
                  <Controller
                      name={`rules.${index}.pattern`}
                      control={control}
                      render={({ field }) => (
                        <FIXFieldsAutocompleteInput
                          fieldSuggestions={fieldSuggestions}
                          value={field.value}
                          inputRef={field.ref}
                          placeholder='Pattern'
                          style={
                            errors.rules?.[index]?.pattern
                              ? autocompleteInputStyleError
                              : dirtyFields['rules']?.[index]?.['pattern']
                                ? autocompleteInputStyleDirty
                                : autocompleteInputStyle
                              }
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                        />  
                      )}
                  />
                  <InputRightElement
                    style={{ 
                      // icon will 'pass' clicks to the input
                      pointerEvents: 'none'
                    }}
                    children={
                      rulesValidation[rule.id]?.inProgress
                        ? <Spinner size='sm'/> :
                      errors['rules']?.[index]?.['pattern']
                        ? <FiAlertTriangle strokeWidth={3} color={red}/>
                        : <CheckIcon color='green.500' />
                    }
                  />
                </InputGroup>
                <FormErrorMessage
                  style={{
                    whiteSpace: 'pre',
                    textAlign: 'left',
                    overflow: 'auto'
                  }}
                  // hide server side validation error message
                ></FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem rowSpan={1} colSpan={2}>
              <FormControl isInvalid={errors['rules']?.[index]?.['policy']}>
                <Controller
                    name={`rules.${index}.policy`}
                    control={control}
                    render={({ field }) => (
                      <ReactSelect
                          placeholder='Policy'
                          styles={customStyles}
                          height={39}
                          ref={field.ref}
                          defaultValue={ field.value ? toOption(field.value) : null }
                          onChange={(selected) => field.onChange(selected?.value)}
                          onBlur={field.onBlur}
                          options={policyOptions}
                          error={errors['rules']?.[index]?.['policy']}
                          dirty={dirtyFields['rules']?.[index]?.['policy']}
                          formatGroupLabel={(groupedOption) => (
                            groupedOption.label
                              ? <hr style={{ marginTop: '-13px' }}/>
                              : undefined
                          )}
                          components={{
                            Option
                          }}
                      />
                    )}
                /> 
                <FormErrorMessage>{errors['rules']?.[index]?.['policy']?.message}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem rowSpan={1} colSpan={3}>
            <FormControl isInvalid={errors['rules']?.[index]?.['routes']}>
            <Controller
                name={`rules.${index}.routes`}
                control={control}
                render={({ field }) => {
                  return <SortableSelect
                    // react-sortable-hoc props:
                    useDragHandle
                    axis="y"
                    onSortEnd={({ oldIndex, newIndex }) => {
                      const newValue = arrayMove(field.value, oldIndex, newIndex);
                      setValue(`rules.${index}.routes`, newValue, { shouldValidate: true, shouldDirty: true })
                    }}
                    distance={4}
                    // small fix for https://github.com/clauderic/react-sortable-hoc/pull/352:
                    getHelperDimensions={({ node }) => node.getBoundingClientRect()}

                    // react-select props:
                    placeholder='Routes'
                    // width={routesSelectWidth}
                    ref={field.ref}
                    value={field.value.map(toOption)}
                    onChange={(selectedValues) => {
                      field.onChange(selectedValues.map((i) => i.value))
                    }}
                    onBlur={field.onBlur}
                    isMulti
                    isClearable={false}
                    options={routesOptions}
                    error={errors['rules']?.[index]?.['routes']}
                    dirty={isRoutesDirty(dirtyFields['rules']?.[index]?.['routes'])}
                    styles={{
                      ...customStyles,
                      valueContainer: (base, state) => ({
                        ...base,
                        ...(state.hasValue && 
                          {
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            display: 'flex',
                            padding: '4px 8px',
                            overflow: 'auto'
                          })
                      })
                    }}
                    components={{
                      MultiValue: SortableMultiValue,
                      MultiValueLabel: SortableMultiValueLabel,
                    }}
                    closeMenuOnSelect={false}
                  />
                }}
            />
            <FormErrorMessage>{errors['rules']?.[index]?.['routes']?.message}</FormErrorMessage>
            </FormControl>
            </GridItem>
            </Grid>
            {/* We propagate generated field id to hidden 'id' field, so it can be used
                to identify rule during validation */}
            <input type="hidden" defaultValue={rule.id} {...register(`rules.${index}.id`)} />
            <Tooltip
              label='Removes this rule'
            >
              <IconButton 
                variant="ghost"
                icon={<FiX size={20} />}
                onClick={()=>{ remove(index) }}
              />
            </Tooltip>
          </HStack>
        ))}
      </ReactSortable>
      </>)}
    </form>
  );
})